import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import AdminLayout from "../../../layouts/AdminLayout";
import Button from "../../../components/ui/Button";
import Modal from "../../../components/ui/Modal";
import Skeleton from "../../../components/ui/Skeleton";
import SearchInput from "../../../components/ui/SearchInput";
import SelectDropdown from "../../../components/ui/SelectDropdown";
import FormField from "../../../components/ui/FormField";
import TextInput from "../../../components/ui/TextInput";
import Textarea from "../../../components/ui/Textarea";
import ToggleSwitch from "../../../components/ui/ToggleSwitch";
import StatusBadge from "../../../components/ui/StatusBadge";
import { EmptyState } from "../../../components/ui/FeedbackStates";
import masterService from "../../../services/masterEntitiesService";
import { getSocieties } from "../../../services/societyService";
import { toast } from "react-toastify";
import {
  Building,
  Globe,
  Plus,
  Edit2,
  Trash2,
  Layers,
  AlertTriangle,
} from "lucide-react";
import styles from "../Masters.module.css";

const PROPERTY_TYPES = [
  { code: "INDUSTRIAL", label: "Industrial & Tech Campus" },
  { code: "COMMERCIAL", label: "Commercial Complex" },
  { code: "RESIDENTIAL", label: "Residential Society" },
  { code: "CO_WORKING", label: "Co-Working Space" },
  { code: "VILLA_COMMUNITY", label: "Gated Villa Community" },
  { code: "RETAIL_MALL", label: "Retail & Mall" },
  { code: "ALL", label: "Universal (All Property Types)" },
];


const ExpandableDescription = ({ description }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    if (!isExpanded) {
      setCanExpand(el.scrollHeight > el.clientHeight + 1);
    }
  }, [description, isExpanded]);

  if (!description) {
    return (
      <p className={styles.descText} style={{ margin: "8px 0 16px 0" }}>
        <span style={{ fontStyle: "italic", color: "var(--color-text-muted)" }}>
          No description provided.
        </span>
      </p>
    );
  }

  return (
    <div className={styles.descWrapper}>
      <p
        ref={textRef}
        className={[
          styles.descText,
          !isExpanded ? styles.descClamped : styles.descExpanded,
        ].join(" ")}
      >
        {description}
      </p>
      {(canExpand || isExpanded) && (
        <button
          type="button"
          className={styles.readMoreBtn}
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded((prev) => !prev);
          }}
        >
          {isExpanded ? "Read less" : "Read more"}
        </button>
      )}
    </div>
  );
};

export default function MasterDepartments() {
  const [departments, setDepartments] = useState([]);
  const [societies, setSocieties] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("ALL_TYPES");
  const [selectedScope, setSelectedScope] = useState("ALL_SCOPES"); // "ALL_SCOPES", "GLOBAL", "SPECIFIC"
  const [selectedPropertyFilter, setSelectedPropertyFilter] = useState("ALL");

  // Add / Edit Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [formName, setFormName] = useState("");
  const [formTypeCode, setFormTypeCode] = useState("INDUSTRIAL");
  const [formScope, setFormScope] = useState("GLOBAL"); // "GLOBAL" or "SPECIFIC"
  const [formPropertyId, setFormPropertyId] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formIsActive, setFormIsActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deptToDelete, setDeptToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Load departments from API
  const loadDepartments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await masterService.getMasterDepartmentTypes();
      setDepartments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading master department types:", err);
      toast.error("Failed to load master departments.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Load societies for property-specific linking
  const loadSocieties = useCallback(async () => {
    try {
      const data = await getSocieties();
      setSocieties(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn("Could not load societies:", err);
    }
  }, []);

  useEffect(() => {
    loadDepartments();
    loadSocieties();
  }, [loadDepartments, loadSocieties]);

  // Filtered list
  const filteredDepartments = useMemo(() => {
    return departments.filter((d) => {
      // Search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchesName = String(d.name || "").toLowerCase().includes(q);
        const matchesDesc = String(d.description || "").toLowerCase().includes(q);
        const matchesProp = String(d.property_name || "").toLowerCase().includes(q);
        if (!matchesName && !matchesDesc && !matchesProp) return false;
      }

      // Property Type
      if (selectedType !== "ALL_TYPES") {
        if (d.property_type_code !== selectedType && d.property_type_code !== "ALL") {
          return false;
        }
      }

      // Scope
      if (selectedScope === "GLOBAL" && d.property_id) return false;
      if (selectedScope === "SPECIFIC" && !d.property_id) return false;

      // Specific Property Filter
      if (selectedPropertyFilter !== "ALL") {
        if (String(d.property_id) !== String(selectedPropertyFilter)) return false;
      }

      return true;
    });
  }, [departments, searchQuery, selectedType, selectedScope, selectedPropertyFilter]);

  // Stats calculation
  const stats = useMemo(() => {
    const total = departments.length;
    const industrial = departments.filter((d) => d.property_type_code === "INDUSTRIAL").length;
    const commercial = departments.filter((d) => d.property_type_code === "COMMERCIAL").length;
    const residential = departments.filter((d) => d.property_type_code === "RESIDENTIAL").length;
    const coworking = departments.filter((d) => d.property_type_code === "CO_WORKING").length;
    const propertySpecific = departments.filter((d) => Boolean(d.property_id)).length;
    return { total, industrial, commercial, residential, coworking, propertySpecific };
  }, [departments]);

  // Open Modal for Add
  const openAddModal = () => {
    setEditingDept(null);
    setFormName("");
    setFormTypeCode(selectedType !== "ALL_TYPES" ? selectedType : "INDUSTRIAL");
    setFormScope("GLOBAL");
    setFormPropertyId(societies.length > 0 ? String(societies[0].society_id) : "");
    setFormDescription("");
    setFormIsActive(true);
    setModalOpen(true);
  };

  // Open Modal for Edit
  const openEditModal = (dept) => {
    setEditingDept(dept);
    setFormName(dept.name || "");
    setFormTypeCode(dept.property_type_code || "INDUSTRIAL");
    setFormScope(dept.property_id ? "SPECIFIC" : "GLOBAL");
    setFormPropertyId(dept.property_id ? String(dept.property_id) : (societies.length > 0 ? String(societies[0].society_id) : ""));
    setFormDescription(dept.description || "");
    setFormIsActive(Boolean(dept.is_active));
    setModalOpen(true);
  };

  // Submit Add or Edit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanName = formName.trim();
    if (!cleanName) {
      toast.error("Department name is required.");
      return;
    }

    if (formScope === "SPECIFIC" && !formPropertyId) {
      toast.error("Please select a specific property to link this department to.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: cleanName,
        property_type_code: formTypeCode,
        property_id: formScope === "SPECIFIC" ? Number(formPropertyId) : null,
        description: formDescription.trim() || null,
        is_active: formIsActive ? 1 : 0,
      };

      if (editingDept) {
        await masterService.updateMasterDepartmentType(editingDept.id, payload);
        toast.success(`Department "${cleanName}" updated successfully!`);
      } else {
        await masterService.createMasterDepartmentType(payload);
        toast.success(`Department "${cleanName}" created successfully!`);
      }

      setModalOpen(false);
      await loadDepartments();
    } catch (err) {
      console.error("Error saving department:", err);
      toast.error("Failed to save department. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Quick Toggle Active Status
  const handleToggleStatus = async (dept) => {
    try {
      const newStatus = !dept.is_active;
      await masterService.updateMasterDepartmentType(dept.id, { is_active: newStatus ? 1 : 0 });
      toast.success(`"${dept.name}" is now ${newStatus ? "Active" : "Inactive"}`);
      setDepartments((prev) =>
        prev.map((d) => (d.id === dept.id ? { ...d, is_active: newStatus ? 1 : 0 } : d))
      );
    } catch (err) {
      console.error("Error toggling status:", err);
      toast.error("Failed to update status.");
    }
  };

  // Confirm Delete
  const handleDeleteConfirm = async () => {
    if (!deptToDelete) return;
    setDeleting(true);
    try {
      await masterService.deleteMasterDepartmentType(deptToDelete.id);
      toast.success(`Department "${deptToDelete.name}" removed successfully.`);
      setDeleteModalOpen(false);
      setDeptToDelete(null);
      await loadDepartments();
    } catch (err) {
      console.error("Error deleting department:", err);
      toast.error("Failed to delete department.");
    } finally {
      setDeleting(false);
    }
  };

  const getTypeBadgeClass = (code) => {
    switch (code) {
      case "INDUSTRIAL":
        return styles.typeBadgeIndustrial;
      case "COMMERCIAL":
        return styles.typeBadgeCommercial;
      case "RESIDENTIAL":
        return styles.typeBadgeResidential;
      case "CO_WORKING":
        return styles.typeBadgeCoworking;
      default:
        return styles.typeBadgeUniversal;
    }
  };

  return (
    <AdminLayout title="Master Departments">
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>
              <Layers size={24} color="#6366f1" />
              Master Departments & Taxonomies
            </h1>
            <p className={styles.subtitle}>
              Configure departments linked to specific property types or custom departments linked to individual properties.
            </p>
          </div>
          <Button variant="primary" onClick={openAddModal}>
            <Plus size={16} style={{ marginRight: "6px" }} /> Add Department
          </Button>
        </div>

        {/* Quick Filter Stat Pills */}
        <div className={styles.statsRow}>
          <div
            className={selectedType === "ALL_TYPES" && selectedScope === "ALL_SCOPES" ? styles.statPillActive : styles.statPill}
            onClick={() => {
              setSelectedType("ALL_TYPES");
              setSelectedScope("ALL_SCOPES");
            }}
          >
            <span>All Departments</span>
            <span className={styles.statCount}>{stats.total}</span>
          </div>
          <div
            className={selectedType === "INDUSTRIAL" ? styles.statPillActive : styles.statPill}
            onClick={() => {
              setSelectedType("INDUSTRIAL");
              setSelectedScope("ALL_SCOPES");
            }}
          >
            <span>Industrial</span>
            <span className={styles.statCount}>{stats.industrial}</span>
          </div>
          <div
            className={selectedType === "COMMERCIAL" ? styles.statPillActive : styles.statPill}
            onClick={() => {
              setSelectedType("COMMERCIAL");
              setSelectedScope("ALL_SCOPES");
            }}
          >
            <span>Commercial</span>
            <span className={styles.statCount}>{stats.commercial}</span>
          </div>
          <div
            className={selectedType === "RESIDENTIAL" ? styles.statPillActive : styles.statPill}
            onClick={() => {
              setSelectedType("RESIDENTIAL");
              setSelectedScope("ALL_SCOPES");
            }}
          >
            <span>Residential</span>
            <span className={styles.statCount}>{stats.residential}</span>
          </div>
          <div
            className={selectedType === "CO_WORKING" ? styles.statPillActive : styles.statPill}
            onClick={() => {
              setSelectedType("CO_WORKING");
              setSelectedScope("ALL_SCOPES");
            }}
          >
            <span>Co-Working</span>
            <span className={styles.statCount}>{stats.coworking}</span>
          </div>
          <div
            className={selectedScope === "SPECIFIC" ? styles.statPillActive : styles.statPill}
            onClick={() => {
              setSelectedScope("SPECIFIC");
              setSelectedType("ALL_TYPES");
            }}
          >
            <Building size={14} />
            <span>Property Specific</span>
            <span className={styles.statCount}>{stats.propertySpecific}</span>
          </div>
        </div>

        {/* Search & Filter Controls */}
        <div className={styles.filterBar} style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap", marginBottom: "20px" }}>
          <div style={{ flex: 1, minWidth: "260px" }}>
            <SearchInput
              placeholder="Search departments by name or description..."
              value={searchQuery}
              onChange={(val) => setSearchQuery(val)}
            />
          </div>

          <div style={{ minWidth: "200px" }}>
            <SelectDropdown
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              options={[
                { label: "All Property Types", value: "ALL_TYPES" },
                ...PROPERTY_TYPES.map((t) => ({ label: t.label, value: t.code })),
              ]}
            />
          </div>

          <div style={{ minWidth: "170px" }}>
            <SelectDropdown
              value={selectedScope}
              onChange={(e) => setSelectedScope(e.target.value)}
              options={[
                { label: "All Scopes", value: "ALL_SCOPES" },
                { label: "Global Only", value: "GLOBAL" },
                { label: "Property Specific Only", value: "SPECIFIC" },
              ]}
            />
          </div>

          {selectedScope === "SPECIFIC" && societies.length > 0 && (
            <div style={{ minWidth: "200px" }}>
              <SelectDropdown
                value={selectedPropertyFilter}
                onChange={(e) => setSelectedPropertyFilter(e.target.value)}
                options={[
                  { label: "All Properties", value: "ALL" },
                  ...societies.map((s) => ({
                    label: s.society_name,
                    value: String(s.society_id),
                  })),
                ]}
              />
            </div>
          )}
        </div>

        {/* Content Grid */}
        {loading ? (
          <div style={{ padding: "24px" }}>
            <Skeleton variant="card" count={4} />
          </div>
        ) : filteredDepartments.length === 0 ? (
          <EmptyState
            title="No Departments Found"
            description={
              searchQuery || selectedType !== "ALL_TYPES" || selectedScope !== "ALL_SCOPES"
                ? "Try adjusting your search criteria or filters."
                : "Get started by adding your first department."
            }
            action={
              <Button variant="primary" onClick={openAddModal}>
                <Plus size={16} style={{ marginRight: "6px" }} /> Add Department
              </Button>
            }
          />
        ) : (
          <div className={styles.grid}>
            {filteredDepartments.map((dept) => (
              <div key={dept.id} className={styles.card}>
                <div>
                  <div className={styles.cardTop}>
                    <div className={styles.cardInfo}>
                      <div className={styles.companyName}>
                        <span style={{ fontSize: "15px", fontWeight: 700, color: "var(--color-text-strong)" }}>
                          {dept.name}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: "6px", alignItems: "center", marginTop: "6px", flexWrap: "wrap" }}>
                        <span className={getTypeBadgeClass(dept.property_type_code)}>
                          {dept.property_type_code}
                        </span>
                        {dept.property_id ? (
                          <span className={styles.scopeProperty} title={`Linked to Property #${dept.property_id}`}>
                            <Building size={11} /> {dept.property_name || `Property #${dept.property_id}`}
                          </span>
                        ) : (
                          <span className={styles.scopeGlobal} title="Available across all properties of this type">
                            <Globe size={11} /> Global
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <ExpandableDescription description={dept.description} />
                </div>

                <div className={styles.cardBottom} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px" }}>
                  <button
                    type="button"
                    className={styles.statusBadgeBtn}
                    onClick={() => handleToggleStatus(dept)}
                    title={`Click to ${dept.is_active ? "deactivate" : "activate"}`}
                  >
                    <StatusBadge
                      status={dept.is_active ? "ACTIVE" : "INACTIVE"}
                      label={dept.is_active ? "Active" : "Inactive"}
                    />
                  </button>

                  <div className={styles.cardActions} style={{ display: "flex", gap: "6px" }}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditModal(dept)}
                      title="Edit Department"
                    >
                      <Edit2 size={13} />
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        setDeptToDelete(dept);
                        setDeleteModalOpen(true);
                      }}
                      title="Delete Department"
                    >
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add / Edit Modal */}
        <Modal
          isOpen={modalOpen}
          onClose={() => !submitting && setModalOpen(false)}
          title={editingDept ? "Edit Department" : "Add Master Department"}
        >
          <form onSubmit={handleSubmit} className={styles.form} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <FormField label="Department / Space Name" required>
              <TextInput
                placeholder="e.g. Technology & Engineering, Civil & Liaison..."
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                autoFocus
              />
            </FormField>

            <FormField
              label="Target Property Type"
              required
              helperText="This department will strictly appear in forms and dropdowns for this property type only."
            >
              <SelectDropdown
                value={formTypeCode}
                onChange={(e) => setFormTypeCode(e.target.value)}
                options={PROPERTY_TYPES.map((t) => ({
                  label: `${t.label} (${t.code})`,
                  value: t.code,
                }))}
              />
            </FormField>

            <FormField label="Department Scope">
              <div className={styles.scopeSelector} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <div
                  className={formScope === "GLOBAL" ? styles.scopeOptionActive : styles.scopeOption}
                  onClick={() => setFormScope("GLOBAL")}
                >
                  <Globe size={16} />
                  <div>
                    <div style={{ fontWeight: 600 }}>Global (All Properties)</div>
                    <div style={{ fontSize: "11px", color: "var(--color-text-muted)" }}>
                      Applies to all properties of {formTypeCode}
                    </div>
                  </div>
                </div>

                <div
                  className={formScope === "SPECIFIC" ? styles.scopeOptionActive : styles.scopeOption}
                  onClick={() => setFormScope("SPECIFIC")}
                >
                  <Building size={16} />
                  <div>
                    <div style={{ fontWeight: 600 }}>Specific Property</div>
                    <div style={{ fontSize: "11px", color: "var(--color-text-muted)" }}>
                      Restricted to a single property
                    </div>
                  </div>
                </div>
              </div>
            </FormField>

            {formScope === "SPECIFIC" && (
              <FormField label="Select Target Property" required>
                <SelectDropdown
                  value={formPropertyId}
                  onChange={(e) => setFormPropertyId(e.target.value)}
                  options={[
                    { label: "-- Choose a property --", value: "" },
                    ...societies.map((s) => ({
                      label: `${s.society_name} (${s.society_type || "Property"}) - #${s.society_id}`,
                      value: String(s.society_id),
                    })),
                  ]}
                />
              </FormField>
            )}

            <FormField label="Description (Optional)">
              <Textarea
                rows={3}
                placeholder="Brief description of functions or operations handled by this department..."
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
              />
            </FormField>

            <ToggleSwitch
              checked={formIsActive}
              onChange={(e) => setFormIsActive(e.target.checked)}
              label="Active in Dropdowns & Forms"
              description="When disabled, this department is hidden from new employee and workstation forms."
            />

            <div className={styles.formActions} style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "12px" }}>
              <Button variant="outline" type="button" onClick={() => setModalOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={submitting} loading={submitting}>
                {editingDept ? "Update Department" : "Create Department"}
              </Button>
            </div>
          </form>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={deleteModalOpen}
          onClose={() => !deleting && setDeleteModalOpen(false)}
          title="Delete Department"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
              <div
                style={{
                  background: "#fee2e2",
                  color: "#dc2626",
                  padding: "10px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <AlertTriangle size={24} />
              </div>
              <div>
                <p style={{ margin: "0 0 6px 0", fontSize: "14px", fontWeight: 600, color: "var(--color-text-strong)" }}>
                  Are you sure you want to delete &quot;{deptToDelete?.name}&quot;?
                </p>
                <p style={{ margin: 0, fontSize: "13px", color: "var(--color-text-muted)", lineHeight: 1.5 }}>
                  This will remove the department from all creation dropdowns and forms for {deptToDelete?.property_type_code}. Existing units or records already linked will not be deleted.
                </p>
              </div>
            </div>

            <div className={styles.formActions} style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <Button variant="outline" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                loading={deleting}
              >
                Delete Department
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
}
